import { formatDate } from "@angular/common";
import { Injectable } from "@angular/core";
import { REGEX } from "@app/shared/constants/regex.constants";
import moment, { Moment } from "moment";

@Injectable({
  providedIn: "root"
})
export class DateService {
  /**
   * Formats a date according to locale rules.
   *
   * @param date
   * @param format
   */
  formatTimeStamp(date: string, format: string, locale: string): string {
    return formatDate(this.timeStampToDate(date), format, locale);
  }

  /**
   * timeStampToDate()
   *
   * Return a date object from a DB2 *ISO time stamp string
   *
   * @param time
   */
  timeStampToDate(time: string): Date {
    let date: Date = new Date();
    if (time !== "") {
      try {
        date = new Date(this.normalizeTimestamp(time));
      } catch {}
    }
    return date;
  }

  /**
   * dateToTimeStamp()
   *
   * Return a DB2 *ISO time stamp string from a date object
   *
   * @param date
   */
  dateToTimeStamp(value: Date | Moment | string): string {
    const date = this._convertToDateObj(value);

    return (
      date.getFullYear() +
      "-" +
      this.twoDigits(1 + date.getMonth()) +
      "-" +
      this.twoDigits(date.getDate()) +
      "-" +
      this.twoDigits(date.getHours()) +
      "." +
      this.twoDigits(date.getMinutes()) +
      "." +
      this.twoDigits(date.getSeconds()) +
      "." +
      new Date().getMilliseconds() +
      "000"
    );
  }

  /**
   * dateToString()
   *
   * Return a date string in ccyymmdd format
   *
   * @param {*} value
   */
  dateToString(value: Date | Moment | string): string {
    let y = "1901";
    let m = "01";
    let d = "01";
    const date = this._convertToDateObj(value);

    try {
      y = date.getFullYear().toString();
      m = (date.getMonth() + 1).toString();
      d = date.getDate().toString();
    } catch {
      y = "1901";
      m = "01";
      d = "01";
    }

    m = m.length > 1 ? m : "0" + m;
    d = d.length > 1 ? d : "0" + d;

    return y + m + d;
  }

  /**
   * Convert ISO date string to valid javascript date.
   * @param value
   * @param format
   * @returns
   */
  isoToDate(value: string, format: string = "MMDDYYYY") {
    if (!value) {
      return "";
    }

    return this._convertToDateObj(value, format);
  }

  /**
   * timeToString()
   *
   * Return hh:mm:ss string from date object
   *
   * @param {*} date
   */
  timeToString(value: Date | Moment | string): string {
    let h = "";
    let m = "";
    let s = "";
    const date = this._convertToDateObj(value);

    try {
      h = date.getHours().toString();
      m = date.getMinutes().toString();
      s = date.getSeconds().toString();
    } catch {
      h = "00";
      m = "00";
      s = "00";
    }

    h = h.length > 1 ? h : "0" + h;
    m = m.length > 1 ? m : "0" + m;
    s = s.length > 1 ? s : "0" + s;

    return h + ":" + m + ":" + s;
  }

  /**
   * stringToDate()
   *
   * Return a date object from ccyymmdd string
   *
   * @param {*} str
   */
  stringToDate(str: string): Date {
    let y = "1901";
    let m = "01";
    let d = "01";

    if (str === undefined) {
      str = "19010101";
    }

    if (str.length == 8) {
      y = str.substring(0, 4);
      m = str.substring(4, 6);
      d = str.substring(6, 8);
    }

    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  }

  /**
   * Combine a date object from a DB2 *ISO and a time string in format HH:MM:YYY into a single
   * a DB2 *ISO date object.
   * @param date
   * @param time
   */
  combineDateTime(dateValue: string | Date | Moment, timeValue: string | Date | Moment): string {
    let date = dateValue;
    let time = timeValue;

    if (dateValue instanceof moment) {
      date = (dateValue as Moment).toDate();
    }

    if (timeValue instanceof moment) {
      time = (timeValue as Moment).toDate();
    }

    return (date as string).substring(0, 11) + (time as string).replace(REGEX.COLON, ".") + ".000000";
  }

  /**
   * Convert a 12 hour time format to a 24 hour time format
   * @param time
   */
  time12HourTo24Hour(time: string, period: "AM" | "PM"): string {
    const tt = time ? time?.split(":") : undefined;
    if (tt?.length) {
      let hh: number = parseInt(tt[0]);
      const mm: number = parseInt(tt[1]);

      if (hh === 0) {
        return time;
      }

      if (period === "PM" && hh >= 1 && hh <= 11) {
        hh += 12;
      } else if (period === "AM" && hh === 12) {
        hh -= 12;
      }

      return this.twoDigits(hh) + ":" + this.twoDigits(mm);
    } else {
      return this.twoDigits(12) + ":" + this.twoDigits(0);
    }
  }

  /**
   * Convert a 24 hour time format to a 12 hour time format
   * @param time
   */
  time24HourTo12Hour(time: string): string {
    const tt = time ? time?.split(":") : undefined;
    if (tt?.length) {
      let hh: number = parseInt(time.split(":")[0]);
      const mm: number = parseInt(time.split(":")[1]);
      const pp = hh > 12 ? "PM" : "AM";

      hh = ((hh + 11) % 12) + 1;
      return this.twoDigits(hh) + ":" + this.twoDigits(mm) + ":" + pp;
    } else {
      return this.twoDigits(12) + ":" + this.twoDigits(0) + ":" + "AM";
    }
  }

  /**
   * Create a new date object
   * @param date
   * @param time24Hour
   */
  createNewDateObject(value: Date | Moment | string, time24Hour: string) {
    const date = this._convertToDateObj(value);
    const mm: number = date.getMonth();
    const dd: number = date.getDate();
    const yy: number = date.getFullYear();
    const h: number = time24Hour ? parseInt(time24Hour.split(":")[0]) : 0;
    const m: number = time24Hour ? parseInt(time24Hour.split(":")[1]) : 0;
    return new Date(yy, mm, dd, h, m, 0, 0);
  }

  /**
   * Return an array of months for the current locale.
   * @param locale
   */
  getLocaleMonthNames(locale: string): string[] {
    const intlFormatter = new Intl.DateTimeFormat(locale, { month: "long" });
    const months: string[] = [];
    for (let i = 1; i <= 12; i++) {
      const date = new Date(Date.UTC(new Date().getFullYear(), i)); // 3th January 2021 is a Sunday
      months.push(intlFormatter.format(date));
    }
    return months;
  }

  /**
   * Return a specific month name based on current locale.
   * @param locale
   * @param month
   * @param izZeroBasedMonth : Determine whether we need to find the correct value in the month array based on month value
   */
  getLocaleMonthName(locale: string, month: number, izZeroBasedMonth?: boolean): string {
    const months = this.getLocaleMonthNames(locale);
    return months[month - (izZeroBasedMonth ? 0 : 1)];
  }

  /**
   * Return an array of day names for the current locale.
   * @param locale
   * @returns
   */
  getLocaleDayNames(locale: string): string[] {
    const intlFormatter = new Intl.DateTimeFormat(locale, { weekday: "long" });
    const dayNames: string[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(Date.UTC(2021, 0, i + 4));
      dayNames.push(intlFormatter.format(date));
    }

    return dayNames;
  }

  /**
   * Return a specific day name based on current locale.
   * @param locale
   * @param day
   */
  getLocaleDayName(locale: string, day: string): string {
    const days = this.getLocaleDayNames(locale);
    return day ? days[parseInt(day)] : "";
  }

  /**
   * Create a proper time value recieved from ngx-material-time-picker
   * and convert it to follow the ngx-mask that is set up for the input fields.
   * @param time
   */
  formatTimeHour(time: string) {
    const hh: string = time.substring(0, time.indexOf(":"));
    const newTime: string = this.twoDigits(parseInt(hh)) + time.substring(hh.length);
    return newTime;
  }

  /**
   * Determine if date value passed in is a valid date value.
   * When values are numeric or string, they are assummed to
   * be in in YYYYMMDD format.
   * @param date
   */
  isValidDate(date: moment.MomentInput): boolean {
    if (date === "00010101") {
      return false;
    }

    return moment(date, "YYYYMMDD", true).isValid();
  }

  /**
   * Determine if the Date object passed in is a value date-timestamp.
   * @param date
   * @returns
   */
  isValidTimeStamp(date: Date) {
    return date instanceof Date && !isNaN(date.getTime());
  }

  get moment() {
    return moment;
  }

  /**
   * normalizeTimestamp()
   *
   * Transform a DB2 *ISO time stamp to a valid javascript time stamp format
   *
   * @param time
   */
  private normalizeTimestamp(time: string): string | number {
    if (time === "") {
      return 0;
    }
    return time.slice(0, 10) + "T" + time.slice(11).split(".").join(":").slice(0, 8);
  }

  /**
   * twoDigits()
   *
   * Return a zero padded two character string from a number
   *
   * @param digits
   */
  private twoDigits(digits: number): string {
    if (0 <= digits && digits < 10) {
      return "0" + digits.toString();
    }

    if (-10 < digits && digits < 0) {
      return "-0" + (-1 * digits).toString();
    }

    return digits.toString();
  }

  /**
   * Convert a moment object to a date object
   * @param date
   */
  private _convertToDateObj(date: Date | Moment | string, format?: moment.MomentFormatSpecification): Date {
    let d = null;
    if (date instanceof moment) {
      d = (date as Moment).toDate();
    } else if (date instanceof Date) {
      d = date as Date;
    } else {
      d = moment(date, format ? format : "", true).toDate();
    }
    return d;
  }
}
